"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ClassicLoader from "@/components/common/classic-loader";
import { FormInput } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { getAvailabilityZone } from "@/features/localizer/api/get-availablity-zone";
import { LocalizerDialog } from "@/features/localizer/components/localizer-dialog";
import { postCodeRegex } from "@/lib/postcode-helper";

const formSchema = z.object({
  postCode: z
    .string()
    .regex(new RegExp(postCodeRegex()), "localizer.form.errors.postCodeFormat")
    .min(1, { message: "localizer.form.errors.postCode" }),
  city: z.string().min(1, { message: "localizer.form.errors.city" }),
});

export const LocalizerForm = () => {
  const t = useTranslations("localizer");
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      postCode: "",
      city: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      await getAvailabilityZone(data);
      setStatus("success");
      setOpen(true);
    } catch (error) {
      console.error(error);
      setStatus("error");
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-10 flex flex-col gap-3 md:mt-6 md:flex-row md:gap-4"
        >
          <FormInput
            form={form}
            fieldName="postCode"
            fieldLabel={t("form.fields.postCode")}
            autoComplete="postal-code"
            autoCorrect="off"
            inputMode="numeric"
            inputClassName="h-14"
            className="md:w-66 md:shrink-0"
          />
          <div className="flex-1">
            <FormInput
              form={form}
              fieldName="city"
              fieldLabel={t("form.fields.city")}
              autoComplete="address-level2"
              autoCorrect="off"
              inputClassName="h-14"
            />
          </div>
          <Button
            type="submit"
            className="mt-3 h-14 px-8 md:mt-0 md:w-40"
            disabled={isLoading}
          >
            {t("form.submit")}
            {isLoading ? (
              <ClassicLoader className="ml-2 h-4 w-4 border-white border-t-transparent" />
            ) : (
              <IconArrowNarrowRight stroke={1.25} className="ml-2" />
            )}
          </Button>
        </form>
      </Form>
      {status && (
        <LocalizerDialog
          open={open}
          onOpenChange={setOpen}
          city={form.getValues("city")}
          postCode={form.getValues("postCode")}
          status={status}
        />
      )}
    </>
  );
};
